import React, { useState } from "react";
import { Send, Mail, Phone, MapPin, CheckCircle2, AlertCircle } from "lucide-react";
import { COMPANY } from "../data/mock";
import { enquiriesApi } from "../lib/api";

const ContactCTA = () => {
  const [state, setState] = useState({ sent: false, error: "", loading: false });
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "ISO 9001", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    setState({ sent: false, error: "", loading: true });
    try {
      await enquiriesApi.create(form);
      setState({ sent: true, error: "", loading: false });
      setForm({ name: "", email: "", phone: "", company: "", service: "ISO 9001", message: "" });
      setTimeout(() => setState((s) => ({ ...s, sent: false })), 5000);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again or call us.";
      setState({ sent: false, error: msg, loading: false });
    }
  };

  return (
    <section className="section-pad bg-white">
      <div className="lis-container grid lg:grid-cols-2 gap-10">
        <div>
          <span className="eyebrow">Get in Touch</span>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold text-slate-900">Start your ISO certification journey today</h2>
          <div className="divider-red mt-4" />
          <p className="mt-4 text-slate-600 max-w-lg">
            Tell us a little about your business and the standard you're interested in. One of our senior consultants will get back to you within one working day.
          </p>

          <div className="mt-8 space-y-5">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-lg bg-red-50 text-brand flex items-center justify-center flex-shrink-0">
                <MapPin size={20} />
              </div>
              <div>
                <div className="font-semibold text-slate-900">Head Office</div>
                <p className="text-sm text-slate-600">{COMPANY.address}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-lg bg-red-50 text-brand flex items-center justify-center flex-shrink-0">
                <Phone size={20} />
              </div>
              <div>
                <div className="font-semibold text-slate-900">Talk to us</div>
                <a href={`tel:${COMPANY.phones[0].replace(/\s/g, "")}`} className="block text-sm text-slate-600 hover:text-brand">{COMPANY.phones[0]}</a>
                <a href={`tel:${COMPANY.phones[1].replace(/\s/g, "")}`} className="block text-sm text-slate-600 hover:text-brand">{COMPANY.phones[1]}</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-lg bg-red-50 text-brand flex items-center justify-center flex-shrink-0">
                <Mail size={20} />
              </div>
              <div>
                <div className="font-semibold text-slate-900">Email us</div>
                <a href={`mailto:${COMPANY.email}`} className="text-sm text-slate-600 hover:text-brand">{COMPANY.email}</a>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-100 rounded-2xl p-6 md:p-8">
          {state.sent && (
            <div className="mb-4 flex items-center gap-2 text-sm bg-emerald-50 text-emerald-700 border border-emerald-200 px-4 py-3 rounded-lg">
              <CheckCircle2 size={16} /> Thanks! Your enquiry has been received. Our team will reach out shortly.
            </div>
          )}
          {state.error && (
            <div className="mb-4 flex items-center gap-2 text-sm bg-red-50 text-red-700 border border-red-200 px-4 py-3 rounded-lg">
              <AlertCircle size={16} /> {state.error}
            </div>
          )}
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-700">Full Name*</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="contact-input mt-1" placeholder="Your name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Company</label>
              <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="contact-input mt-1" placeholder="Company name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Email*</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="contact-input mt-1" placeholder="you@company.com" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Phone*</label>
              <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="contact-input mt-1" placeholder="+91 ..." />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-700">Standard of Interest</label>
              <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="contact-input mt-1">
                {["ISO 9001", "ISO 14001", "ISO 45001", "ISO 27001", "ISO 22000 / FSSC", "ISO 13485", "IATF 16949", "NABL ISO 17025", "SOC 2", "GDPR / DPDP", "Other"].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-slate-700">Message</label>
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="contact-input mt-1" rows={4} placeholder="Tell us about your requirement..." />
            </div>
            <div className="sm:col-span-2">
              <button type="submit" disabled={state.loading} className="btn-brand w-full justify-center disabled:opacity-70 disabled:cursor-not-allowed">
                {state.loading ? "Sending..." : (<>Send Enquiry <Send size={16} /></>)}
              </button>
              <p className="text-[11px] text-slate-500 mt-2 text-center">By submitting, you agree to our privacy policy. We never share your data.</p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactCTA;
